import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ReactSelect } from "@/components/ui/react-select";
import {
  CheckCircle,
  AlertTriangle,
  Package,
  DollarSign,
  Loader2,
} from "lucide-react";
import warrantyApi from "@/api/warrantyApi";
import { API_ENDPOINTS } from "@/api/header";
import apiClient from "@/api/apiClient";
import {
  WARRANTY_RETURN_CONDITIONS,
  WARRANTY_RESOLUTION_TYPES,
} from "@/lib/warrantyUtils";

const ApproveClaimForm = ({ claim, onOpenChange, onDone }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (perm) => permissions.includes(perm);

  const canOverrideWarranty = hasPermission("warranty claim override_warranty");
  const canIssueRefund = hasPermission("warranty claim refund");

  // Form states
  const [resolutionType, setResolutionType] = useState("replacement");
  const [returnCondition, setReturnCondition] = useState("defective");
  const [notes, setNotes] = useState("");

  // Expired warranty override
  const [isOverrideEnabled, setIsOverrideEnabled] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  // Refund specific
  const [refundJustification, setRefundJustification] = useState("");

  // Replacement specific
  const [isUpgradeDowngrade, setIsUpgradeDowngrade] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(claim?.product || null);

  const originalVal =
    claim?.sale_item?.unit_price ||
    claim?.sale_item?.price ||
    claim?.product?.price ||
    claim?.product?.selling_price ||
    0;
  const [unitPrice, setUnitPrice] = useState(String(originalVal));

  const [selectedSerial, setSelectedSerial] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [manualSerialInput, setManualSerialInput] = useState(false);
  const [customSerialText, setCustomSerialText] = useState("");

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      return String(dateStr).split("T")[0];
    } catch {
      return "";
    }
  };

  const [warrantyStartDate, setWarrantyStartDate] = useState(() => {
    return formatDateForInput(claim?.warranty_start_date) || new Date().toISOString().split("T")[0];
  });
  const [warrantyEndDate, setWarrantyEndDate] = useState(() => {
    return formatDateForInput(claim?.warranty_end_date);
  });

  const originalProduct = claim?.product;
  const originalStoreId = claim?.store_id || currentStore?.id;
  const targetProductId = selectedProduct?.id || originalProduct?.id;
  const activeProduct = isUpgradeDowngrade ? selectedProduct : (originalProduct || selectedProduct);

  // Fetch products for upgrade/downgrade selector
  const { data: productsData = [], isLoading: isProductsLoading } = useQuery({
    queryKey: ["products-replacement-list"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.PRODUCTS, {
        params: { per_page: 100 },
      });
      return res.data?.data || res.data || [];
    },
    enabled: isUpgradeDowngrade,
  });

  // Fetch available serials for target product & store
  const { data: availableSerials = [], isLoading: isLoadingSerials } = useQuery({
    queryKey: ["available-serials", targetProductId, originalStoreId],
    queryFn: async () => {
      if (!targetProductId || !originalStoreId) return [];
      const res = await warrantyApi.getAvailableSerials(targetProductId, originalStoreId);
      return res.data?.data || res.data || [];
    },
    enabled: resolutionType === "replacement" && Boolean(targetProductId && originalStoreId),
  });

  // Fetch available batches for target product & store
  const { data: availableBatches = [], isLoading: isLoadingBatches } = useQuery({
    queryKey: ["available-batches", targetProductId, originalStoreId],
    queryFn: async () => {
      if (!targetProductId || !originalStoreId) return [];
      try {
        const res = await warrantyApi.getAvailableBatches(targetProductId, originalStoreId);
        return res.data?.data || res.data || [];
      } catch {
        return [];
      }
    },
    enabled: resolutionType === "replacement" && Boolean(targetProductId && originalStoreId),
  });

  // A product can only be tracked by serial OR batch at one time, never both
  const trackingType = useMemo(() => {
    if (activeProduct?.tracking_type === "serial" || Boolean(activeProduct?.is_serialized)) {
      return "serial";
    }
    if (activeProduct?.tracking_type === "standard") {
      return "batch";
    }
    if (activeProduct?.tracking_type === "none") {
      return "none";
    }

    // Fallback: If not upgraded, check if claim originated with a serial number or batch
    if (!isUpgradeDowngrade) {
      if (claim?.serial_number) return "serial";
      if (claim?.inventory_batch_id || claim?.batch_number) return "batch";
    }

    // Fallback: check available stock data
    if (availableSerials?.length > 0 && availableBatches?.length === 0) return "serial";
    if (availableBatches?.length > 0 && availableSerials?.length === 0) return "batch";
    if (availableSerials?.length > 0) return "serial";
    if (availableBatches?.length > 0) return "batch";

    return "none";
  }, [
    activeProduct?.tracking_type,
    activeProduct?.is_serialized,
    isUpgradeDowngrade,
    claim?.serial_number,
    claim?.inventory_batch_id,
    claim?.batch_number,
    availableSerials?.length,
    availableBatches?.length,
  ]);

  const isSerialTracked = trackingType === "serial";
  const isBatchTracked = trackingType === "batch";

  const claimSerialNumber = claim?.serial_number;
  // Filter out original serial from replacement candidates
  const filteredSerials = useMemo(() => {
    if (!Array.isArray(availableSerials)) return [];
    const origSn = String(claimSerialNumber || "").trim().toLowerCase();
    return availableSerials.filter((s) => {
      const sn = String(typeof s === "string" ? s : s?.serial_number || s?.serial || "");
      if (!sn || !sn.trim()) return false;
      if (origSn && sn.trim().toLowerCase() === origSn) return false;
      return true;
    });
  }, [availableSerials, claimSerialNumber]);

  // Price difference calculation (unit_price - original_item_value)
  const originalItemValue = useMemo(() => {
    return parseFloat(
      claim?.sale_item?.unit_price ??
      claim?.sale_item?.price ??
      claim?.product?.selling_price ??
      claim?.product?.price ??
      0,
    );
  }, [claim]);

  const newUnitPrice = parseFloat(unitPrice || "0");
  const priceDifference = useMemo(() => {
    if (isNaN(newUnitPrice) || isNaN(originalItemValue)) return 0;
    return newUnitPrice - originalItemValue;
  }, [newUnitPrice, originalItemValue]);

  const isWarrantyExpired = claim && claim.is_warranty_valid === false;

  const approveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        resolution_type: resolutionType,
        return_condition: returnCondition || "defective",
        notes: notes.trim() || null,
      };

      if (isWarrantyExpired && isOverrideEnabled) {
        payload.override_warranty = true;
        payload.override_reason = overrideReason.trim();
        if (overrideReason.trim()) {
          payload.notes = notes.trim()
            ? `${notes.trim()}\n[Warranty Override]: ${overrideReason.trim()}`
            : `[Warranty Override]: ${overrideReason.trim()}`;
        }
      }

      if (resolutionType === "refund") {
        payload.refund_justification = refundJustification.trim();
      }

      if (resolutionType === "replacement") {
        if (isUpgradeDowngrade && selectedProduct?.id) {
          payload.replacement_product_id = selectedProduct.id;
          payload.unit_price = newUnitPrice;
        }

        if (isSerialTracked) {
          if (selectedSerial?.id) {
            payload.replacement_serial_id = selectedSerial.id;
            payload.replacement_serial_number = selectedSerial.serial_number;
          } else if (customSerialText.trim()) {
            payload.replacement_serial_number = customSerialText.trim();
          }
        } else if (isBatchTracked) {
          if (selectedBatch?.id) {
            payload.inventory_batch_id = selectedBatch.id;
          }
        }

        if (warrantyStartDate) payload.warranty_start_date = warrantyStartDate;
        if (warrantyEndDate) payload.warranty_end_date = warrantyEndDate;
      }

      const res = await warrantyApi.approve(claim.id, payload);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success("Warranty claim approved successfully");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }

      const result = data?.data || data;
      // Step 3b handling: replacement_sale with payment pending or credit note
      if (result?.replacement_sale) {
        const sale = result.replacement_sale;
        const total = parseFloat(sale.total || sale.grand_total || 0);
        if (total > 0 || sale.payment_status === "pending") {
          toast.info(
            `Replacement sale #${sale.invoice_number || sale.id} created. Amount due: $${total.toFixed(2)}.`,
            {
              action: {
                label: "Go to Payment",
                onClick: () => navigate(`/pos?sale_id=${sale.id}&payment=true`),
              },
              duration: 10000,
            },
          );
        }
      }

      if (result?.credit_note_id || result?.credit_note) {
        const creditNoteId = result.credit_note_id || result.credit_note?.id;
        toast.info("Credit note generated for price difference.", {
          action: {
            label: "Print Credit Note",
            onClick: () =>
              warrantyApi.openPdfReport(warrantyApi.pdf.creditNote(creditNoteId)),
          },
          duration: 10000,
        });
      }

      onOpenChange(false);
      if (onDone) onDone(result);
    },
    onError: (error) => {
      const msg = error.response?.data?.message || "Failed to approve warranty claim";
      toast.error(msg);
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isWarrantyExpired && !canOverrideWarranty) {
      toast.error(
        "Warranty period has expired for this claim. Approval is blocked without override permission.",
      );
      return;
    }

    if (isWarrantyExpired && isOverrideEnabled && !overrideReason.trim()) {
      toast.error("Please provide a reason for overriding the expired warranty");
      return;
    }

    if (resolutionType === "refund") {
      if (!canIssueRefund) {
        toast.error("You are not allowed to issue warranty refunds");
        return;
      }
      if (!refundJustification.trim()) {
        toast.error("A refund justification is required");
        return;
      }
    }

    approveMutation.mutate();
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-600 dark:text-emerald-400">
            <CheckCircle className="h-5 w-5" />
          </div>
          <div>
            <DialogTitle>Approve Claim #{claim.claim_number}</DialogTitle>
            <DialogDescription>
              Select remedy, specify return condition, and configure replacement or refund
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      {/* Expired Warranty Guard Alert */}
      {isWarrantyExpired && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3.5 text-xs text-amber-900 dark:text-amber-200 space-y-2">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
            <span>Warranty Expired Warning</span>
          </div>
          <p className="text-muted-foreground dark:text-amber-300/80">
            The warranty period for this claim has expired. Standard approval will return 422
            unless overridden by an authorized user.
          </p>
          {canOverrideWarranty ? (
            <div className="pt-2 border-t border-amber-500/20 space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="override-switch" className="font-medium cursor-pointer">
                  Enable Warranty Period Override
                </Label>
                <Switch
                  id="override-switch"
                  checked={isOverrideEnabled}
                  onCheckedChange={setIsOverrideEnabled}
                />
              </div>
              {isOverrideEnabled && (
                <div className="space-y-1 pt-1">
                  <Label htmlFor="override_reason" className="text-[11px] text-foreground font-semibold">
                    Override Justification *
                  </Label>
                  <Input
                    id="override_reason"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="Explain why this expired warranty is being approved..."
                    required
                  />
                </div>
              )}
            </div>
          ) : (
            <p className="text-destructive font-medium pt-1">
              You do not have permission ('warranty claim override_warranty') to approve expired
              claims.
            </p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 py-1">
        {/* Remedy & Return Condition in compact 2-column grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="resolution_type" className="text-xs font-medium">Remedy *</Label>
            <Select value={resolutionType} onValueChange={setResolutionType}>
              <SelectTrigger id="resolution_type" className="h-9 text-xs">
                <SelectValue placeholder="Select remedy" />
              </SelectTrigger>
              <SelectContent>
                {WARRANTY_RESOLUTION_TYPES.map((res) => (
                  <SelectItem
                    key={res.value}
                    value={res.value}
                    disabled={res.value === "refund" && !canIssueRefund}
                    className="text-xs"
                  >
                    {res.label}{" "}
                    {res.value === "refund" && !canIssueRefund ? "(Permission required)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="return_condition" className="text-xs font-medium">Return Condition *</Label>
            <Select value={returnCondition} onValueChange={setReturnCondition}>
              <SelectTrigger id="return_condition" className="h-9 text-xs">
                <SelectValue placeholder="Select return condition" />
              </SelectTrigger>
              <SelectContent>
                {WARRANTY_RETURN_CONDITIONS.map((cond) => (
                  <SelectItem key={cond.value} value={cond.value} className="text-xs">
                    {cond.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Replacement Options */}
        {resolutionType === "replacement" && (
          <div className="rounded-lg border bg-muted/20 p-3.5 space-y-3">
            <div className="flex items-center justify-between pb-2 border-b">
              <div className="flex items-center gap-2 min-w-0">
                <Package className="h-4 w-4 text-primary shrink-0" />
                <span className="text-xs font-semibold">Replacement Unit</span>
                {!isUpgradeDowngrade && originalProduct?.name && (
                  <Badge variant="outline" className="text-[11px] font-normal truncate max-w-[240px]">
                    {originalProduct.name}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Label htmlFor="upgrade-toggle" className="text-xs text-muted-foreground cursor-pointer font-normal">
                  Upgrade / Downgrade
                </Label>
                <Switch
                  id="upgrade-toggle"
                  checked={isUpgradeDowngrade}
                  onCheckedChange={(val) => {
                    setIsUpgradeDowngrade(val);
                    if (!val) {
                      setSelectedProduct(originalProduct);
                      setUnitPrice(String(originalItemValue));
                    }
                  }}
                />
              </div>
            </div>

            {/* Upgrade Product Picker & Price Difference */}
            {isUpgradeDowngrade && (
              <div className="space-y-3 pt-0.5">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Replacement Product *</Label>
                  <ReactSelect
                    isLoading={isProductsLoading}
                    placeholder="Search replacement product..."
                    value={
                      selectedProduct
                        ? { value: selectedProduct.id, label: selectedProduct.name }
                        : null
                    }
                    onChange={(opt) => {
                      if (!opt) {
                        setSelectedProduct(null);
                        return;
                      }
                      const p = productsData.find((item) => item.id === opt.value);
                      setSelectedProduct(p || { id: opt.value, name: opt.label });
                      if (p?.price || p?.selling_price) {
                        setUnitPrice(String(p.price || p.selling_price));
                      }
                      setSelectedSerial(null);
                      setSelectedBatch(null);
                    }}
                    options={productsData.map((p) => ({
                      value: p.id,
                      label: `${p.name} ${p.sku ? `(${p.sku})` : ""}`,
                    }))}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                  <div className="space-y-1">
                    <Label htmlFor="unit_price" className="text-xs font-medium">
                      Replacement Unit Price ($)
                    </Label>
                    <Input
                      id="unit_price"
                      type="number"
                      step="0.01"
                      className="h-9 text-xs"
                      value={unitPrice}
                      onChange={(e) => setUnitPrice(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="p-2 rounded border bg-background text-xs space-y-0.5">
                    <div className="flex justify-between text-muted-foreground text-[11px]">
                      <span>Original Value:</span>
                      <span>${originalItemValue.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Price Difference:</span>
                      <span
                        className={
                          priceDifference > 0
                            ? "text-amber-600 dark:text-amber-400"
                            : priceDifference < 0
                              ? "text-blue-600 dark:text-blue-400"
                              : "text-emerald-600 dark:text-emerald-400"
                        }
                      >
                        {priceDifference > 0
                          ? `+$${priceDifference.toFixed(2)} (Due from customer)`
                          : priceDifference < 0
                            ? `-$${Math.abs(priceDifference).toFixed(2)} (Credit note)`
                            : "$0.00 (Settled)"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tracking: Serial OR Batch (Mutually Exclusive) */}
            {isSerialTracked && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Replacement Serial</Label>
                  {filteredSerials.length > 0 && (
                    <button
                      type="button"
                      className="text-[11px] text-primary hover:underline font-medium"
                      onClick={() => setManualSerialInput(!manualSerialInput)}
                    >
                      {manualSerialInput ? "Select from stock" : "Type manual serial"}
                    </button>
                  )}
                </div>

                {manualSerialInput || filteredSerials.length === 0 ? (
                  <Input
                    value={customSerialText}
                    onChange={(e) => setCustomSerialText(e.target.value)}
                    placeholder="Enter serial or leave blank to defer fulfilment..."
                    className="h-9 text-xs font-mono"
                  />
                ) : (
                  <ReactSelect
                    isClearable
                    isLoading={isLoadingSerials}
                    placeholder={
                      isLoadingSerials
                        ? "Loading available stock serials..."
                        : "Select available serial in stock (or leave blank to defer)..."
                    }
                    value={
                      selectedSerial
                        ? {
                          value: selectedSerial.id,
                          label: selectedSerial.serial_number,
                          status: selectedSerial.status,
                        }
                        : null
                    }
                    onChange={(opt) => {
                      if (!opt) {
                        setSelectedSerial(null);
                        return;
                      }
                      const serialObj = filteredSerials.find((s) => s.id === opt.value);
                      setSelectedSerial(serialObj || { id: opt.value, serial_number: opt.label });
                    }}
                    options={filteredSerials.map((s) => ({
                      value: s.id,
                      label: s.serial_number || s.code || s.id,
                      status: s.status_info?.label || s.status,
                    }))}
                    formatOptionLabel={(option) => (
                      <div className="flex justify-between items-center w-full">
                        <span className="font-mono text-xs">{option.label}</span>
                        {option.status && (
                          <Badge
                            variant="outline"
                            className="text-[10px] h-4 text-emerald-600 bg-emerald-50 border-emerald-200"
                          >
                            {option.status}
                          </Badge>
                        )}
                      </div>
                    )}
                  />
                )}
                <div className="text-[11px] text-muted-foreground flex justify-between items-center px-0.5">
                  <span>
                    {filteredSerials.length > 0
                      ? `${filteredSerials.length} available serial(s) in store`
                      : "No serialized units currently in store (Can be fulfilled later)"}
                  </span>
                  {claim.serial_number && (
                    <span className="font-mono">Original SN: {claim.serial_number}</span>
                  )}
                </div>
              </div>
            )}

            {isBatchTracked && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Inventory Batch</Label>
                <ReactSelect
                  isClearable
                  isLoading={isLoadingBatches}
                  placeholder={isLoadingBatches ? "Loading batches..." : "Select batch..."}
                  value={
                    selectedBatch
                      ? {
                        value: selectedBatch.id,
                        label: `${selectedBatch.batch_number} (Qty: ${selectedBatch.quantity_available})`,
                      }
                      : null
                  }
                  onChange={(opt) => {
                    if (!opt) return setSelectedBatch(null);
                    const b = availableBatches.find((item) => item.id === opt.value);
                    setSelectedBatch(b || { id: opt.value });
                  }}
                  options={availableBatches.map((b) => ({
                    value: b.id,
                    label: `${b.batch_number || b.id} - Available: ${b.quantity_available || b.quantity}`,
                  }))}
                />
                {availableBatches.length === 0 && !isLoadingBatches && (
                  <div className="text-[11px] text-muted-foreground px-0.5">
                    No active batches found in store (Can be fulfilled later)
                  </div>
                )}
              </div>
            )}

            {/* Warranty window for replacement (Defaults to original claim dates) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-0.5">
              <div className="space-y-1">
                <Label htmlFor="warranty_start" className="text-xs font-medium">
                  Warranty Start Date
                </Label>
                <Input
                  id="warranty_start"
                  type="date"
                  className="h-9 text-xs"
                  value={warrantyStartDate}
                  onChange={(e) => setWarrantyStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="warranty_end" className="text-xs font-medium">
                  Warranty End Date
                </Label>
                <Input
                  id="warranty_end"
                  type="date"
                  className="h-9 text-xs"
                  value={warrantyEndDate}
                  onChange={(e) => setWarrantyEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {/* 3c. Refund Fields */}
        {resolutionType === "refund" && (
          <div className="rounded-lg border bg-blue-500/10 border-blue-500/30 p-3.5 space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-blue-900 dark:text-blue-300">
              <DollarSign className="h-4 w-4" />
              <span>Refund Remedy Details</span>
            </div>
            <div className="space-y-1">
              <Label htmlFor="refund_justification" className="text-xs font-medium">
                Refund Justification *
              </Label>
              <Textarea
                id="refund_justification"
                value={refundJustification}
                onChange={(e) => setRefundJustification(e.target.value)}
                placeholder="e.g., Replacement unavailable within SLA; customer requested refund per warranty terms..."
                rows={2}
                className="text-xs"
                required
              />
            </div>
            {!claim.sale_id && (
              <div className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1.5 pt-0.5">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  Warning: Claim is not linked to an original sale invoice. Approval will succeed,
                  but no automated credit note will be issued.
                </span>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div className="space-y-1.5">
          <Label htmlFor="notes" className="text-xs font-medium">
            Internal Approval Notes <span className="text-muted-foreground font-normal">(Optional)</span>
          </Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any internal resolution instructions or customer remarks..."
            rows={2}
            className="text-xs resize-none"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={approveMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              approveMutation.isPending || (isWarrantyExpired && !canOverrideWarranty)
            }
          >
            {approveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Approving...
              </>
            ) : (
              "Approve Claim"
            )}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

ApproveClaimForm.propTypes = {
  claim: PropTypes.object.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onDone: PropTypes.func,
};

const ApproveClaimModal = ({ open, onOpenChange, claim, onDone }) => {
  if (!claim) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto">
        {open && (
          <ApproveClaimForm
            key={claim.id || "approve"}
            claim={claim}
            onOpenChange={onOpenChange}
            onDone={onDone}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

ApproveClaimModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  claim: PropTypes.object,
  onDone: PropTypes.func,
};

export default ApproveClaimModal;
